<template>
  <b-row class="m-3">
    <b-col>
      <b-card-group deck>
        <b-card class="border-0">
          The Member Portal allows members to access important information about class schedules and time cards. Training Centers have the option to
          publish their current class schedule. Members can view the schedule and submit a registration request for classes being offered at their
          Local Training Center, and download a copy of their Training History. Apprentices also have the option to track the classes they must attend
          to complete the apprenticeship, as well as record and review On‐Job‐Training hours through this website.
        </b-card>
        <b-card bg-variant="light" @keyup.enter="login" no-body v-if="authChecked">
          <b-alert v-if="passwordResetSuccess" show variant="success" class="mb-0" style="border-radius:0;border-bottom:1px solid #c3e6cb;">
            <p class="mb-0"><strong>Password updated. Please log in.</strong></p>
          </b-alert>
          <b-alert v-else-if="accountRecoverySuccess" show variant="success" class="mb-0" style="border-radius:0;border-bottom:1px solid #c3e6cb;">
            <p class="mb-0"><strong>Account updated. Please log in with your new credentials.</strong></p>
          </b-alert>
          <b-alert v-else-if="registrationSuccess" show variant="success" class="mb-0" style="border-radius:0;border-bottom:1px solid #c3e6cb;">
            <p :class="emailUpdated ? 'mb-2' : 'mb-0'"><strong>Your account has been created successfully. Please log in.</strong></p>
            <p v-if="emailUpdated" class="mb-0" style="font-size:0.875rem;">
              You recently requested to change your email address. It takes up to 10 minutes to process this request, during which time you will not
              be able to reset your password or request your Username. We apologize for this inconvenience. If you have additional questions about why
              you are receiving this message, please contact us.
            </p>
          </b-alert>
          <b-card-body>
            <b-form-group label="Identity:">
              <b-input size="sm" type="text" placeholder="Username, Email Address, or UBC Member ID" v-model="identity" trim />
            </b-form-group>
            <b-form-group label="Password:">
              <b-input-group>
                <b-input size="sm" :type="passwordVisible ? 'text' : 'password'" placeholder="Password" v-model="password" />
                <b-input-group-addon>
                  <b-button size="sm" @click="passwordVisible = !passwordVisible">
                    <b-icon :icon="passwordVisible ? 'eye' : 'eye-slash'" />
                  </b-button>
                </b-input-group-addon>
              </b-input-group>
            </b-form-group>
            <b-form-group class="text-right">
              <b-button size="sm" variant="primary" @click="login">Log In</b-button>
            </b-form-group>
            <b-form-group class="text-right mb-0">
              <small>
                <b-link :to="{ name: 'account-creation' }">Create Account</b-link>
                &nbsp;&bull;&nbsp;
                <b-link :to="{ name: 'request-username' }">Forgot Username?</b-link>
                &nbsp;&bull;&nbsp;
                <b-link :to="{ name: 'request-password' }">Forgot Password?</b-link>
                &nbsp;&bull;&nbsp;
                <b-link :to="{ name: 'faq' }">FAQ</b-link>
                &nbsp;&bull;&nbsp;
                <b-link :to="{ name: 'contact-us' }">Contact Us</b-link>
              </small>
            </b-form-group>
          </b-card-body>
        </b-card>
        <b-card bg-variant="light" no-body v-else>
          <b-card-body>
            <h3>Validating your session ... Please wait.</h3>
          </b-card-body>
        </b-card>
      </b-card-group>
    </b-col>
  </b-row>
</template>
<script>
import { Vue, Component } from 'vue-property-decorator';
import { renderErrorBody } from '@/util/error';

@Component({
  metaInfo: {
    title: 'Member Portal - Log In'
  }
})
export default class LoginForm extends Vue {
  passwordVisible = false;

  identity = null;
  password = null;

  get passwordResetSuccess() {
    return this.$route.query.password_reset === '1';
  }

  get accountRecoverySuccess() {
    return this.$route.query.account_recovered === '1';
  }

  get registrationSuccess() {
    return this.$route.query.registered === '1';
  }

  get emailUpdated() {
    return this.$route.query.email_updated === '1';
  }

  get authChecked() {
    return this.$store.getters['session/authChecked'];
  }

  get desiredRoute() {
    return this.$store.getters.desiredRoute;
  }

  set desiredRoute(route) {
    this.$store.dispatch('redirectSecureRoute', route);
  }

  async login() {
    try {
      const credentials = {
        identity: (this.identity || '').trim(),
        password: (this.password || '').trim()
      };

      if (0 === credentials.identity.length) {
        return;
      }

      const redirectUrl = this.$route?.query?.redirect;
      credentials.redirect = !!redirectUrl;

      const user = await this.$store.dispatch('session/login', credentials);

      if (credentials.redirect) {
        return (document.location = redirectUrl);
      }

      await this.hydrateUserAndRedirect(user);
    } catch (error) {
      const body = renderErrorBody(this.$createElement, (error && error.message) || 'An unknown error occurred, please try again.');
      this.$bvModal.msgBoxOk(body, {
        title: 'Unable to Log In',
        okTitle: 'OK',
        okVariant: 'danger',
        centered: true,
        headerBgVariant: 'danger',
        headerTextVariant: 'white'
      });
    }
  }

  async hydrateUserAndRedirect(user) {
    if (user && user.ubcId) {
      this.$store.dispatch('loadCurrentMember', { ubcId: user.ubcId, override: true });
      this.$store.dispatch('loadTrainingCenter', { ubcId: user.ubcId, override: true });
      this.$store.dispatch('loadMemberPicture', { ubcId: user.ubcId, override: true });
      const route = this.desiredRoute;
      if (route) {
        this.desiredRoute = null;
        this.$router.push(route);
      } else {
        this.$router.push({ name: 'training', params: { ubcId: user.ubcId } });
      }
    }
  }

  async validateSession() {
    const user = await this.$store.dispatch('session/validateCookie', { override: true, route: this.$route });
    await this.hydrateUserAndRedirect(user);
  }
}
</script>
<style scoped></style>
